import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { isSupabaseConfigured, QUERY_STALE_TIME } from '@/config';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  getBracketLeaderboard,
  getMyBracket,
  getMyBracketPrize,
  getMyBracketStats,
  saveBracketPrediction,
} from '@/lib/data/provider';
import type { BracketPayload } from '@/lib/supabase/types';

/** The signed-in user's saved World Cup bracket (null until they predict). */
export function useMyBracket() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['bracket', 'mine', userId ?? 'anon'],
    queryFn: getMyBracket,
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}

export function useSaveBracket() {
  const { userId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { seasonId: string | null; payload: BracketPayload }) =>
      saveBracketPrediction(input.seasonId, input.payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bracket', 'mine', userId ?? 'anon'] });
      queryClient.invalidateQueries({ queryKey: ['bracket', 'leaderboard'] });
    },
  });
}

export function useBracketLeaderboard(enabled = true) {
  return useQuery({
    queryKey: ['bracket', 'leaderboard'],
    queryFn: () => getBracketLeaderboard(),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && enabled,
  });
}

export function useMyBracketStats() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['bracket', 'my-stats', userId ?? 'anon'],
    queryFn: getMyBracketStats,
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}

/** The caller's WC bracket prize, once the final is settled (null otherwise). */
export function useMyBracketPrize() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['bracket', 'my-prize', userId ?? 'anon'],
    queryFn: getMyBracketPrize,
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}
